import { useNavigate } from 'react-router';
import { MarketSelectableCard, MarketStatusPill } from '~/features/shared/market-content';
import { buildProviderViewUrl } from '~/routing';
import type { B2BResponseProvider, B2BResponsesSide } from '../types';

type Props = {
  responseId: number;
  providers?: B2BResponseProvider[];
  responseUnmasked?: boolean;
  /** [MCV-1889] Carried to the provider page so its back link returns to the right Responses tab. */
  side?: B2BResponsesSide;
};

export const ResponseProviderCards = ({
  responseId,
  providers = [],
  responseUnmasked = false,
  side,
}: Props) => {
  if (providers.length === 0) {
    // MCV-1851: a compact one-line row, so the Negotiation section below sits right under the
    // Providers heading instead of under a tall empty panel.
    return (
      <p
        data-cy="response-providers-empty"
        className="rounded-lg border border-dashed border-grey-800 px-4 py-3 text-center text-sm text-content-tertiary"
      >
        No providers attached
      </p>
    );
  }

  return (
    <NavigableResponseProviderCards
      responseId={responseId}
      providers={providers}
      responseUnmasked={responseUnmasked}
      side={side}
    />
  );
};

const NavigableResponseProviderCards = ({
  responseId,
  providers,
  responseUnmasked,
  side,
}: Required<Pick<Props, 'responseId' | 'providers' | 'responseUnmasked'>> &
  Pick<Props, 'side'>) => {
  const navigate = useNavigate();

  return (
    <div className="space-y-2">
      {providers.map((provider) => (
        <MarketSelectableCard
          key={provider.responseProviderId}
          selected={false}
          className="p-3"
          onClick={() => {
            if (provider.providerId != null) {
              navigate(
                buildProviderViewUrl({
                  responseProviderId: provider.responseProviderId,
                  providerId: provider.providerId,
                  responseId,
                  side,
                })
              );
              return;
            }

            navigate(
              buildProviderViewUrl({ responseProviderId: provider.responseProviderId, side })
            );
          }}
          dataCy="response-provider-card"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-base font-semibold text-black">{provider.displayName}</p>
              <p className="text-xs text-grey-500">
                {[provider.providerType, provider.providerCategory].filter(Boolean).join(' / ') ||
                  'Provider'}
              </p>
            </div>
            <MarketStatusPill
              status={provider.masked || !responseUnmasked ? 'masked' : 'unmasked'}
              label={
                provider.masked
                  ? 'Masked'
                  : responseUnmasked
                    ? 'Unmasked'
                    : 'Masked to listing owner'
              }
            />
          </div>
          {provider.npiNum != null && (
            <p className="mt-2 text-xs text-grey-500">NPI {provider.npiNum}</p>
          )}
        </MarketSelectableCard>
      ))}
    </div>
  );
};
